import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, InlineNotification, SkeletonText } from "@carbon/react";
import AuthLayout from "../../components/carbon/AuthLayout/AuthLayout";
import {
  useCompanyReviewDetails,
  useRespondCompanyReview,
} from "../../hooks/useCompanyReviewToken";
import { CompanyReviewStatus, CompanyReviewSummary } from "../../types";
import { formatDate } from "../../utils/utils";
import "./companyReview.scss";

type Outcome = "approved" | "rejected" | "invalid" | "alreadyDecided";

const OPEN: CompanyReviewStatus[] = ["PENDING_COMPANY", "ESCALATED"];

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

const Fact = ({ label, value }: { label: string; value: string | null }) =>
  value ? (
    <div className="company-review__fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  ) : null;

/**
 * PUBLIC, no-login page behind the link in the company review email (`/company-review/:token`).
 * Someone quoted by mail or WhatsApp in the name of the admin's company; the admin says whether
 * they belong to it. Approving adds them as a user and sends the quote to the buyer; rejecting
 * discards it. Rejecting asks for a second click. An unknown token or a question already decided
 * — by another admin or by Simbuy — is a friendly state, never a crash.
 */
const CompanyReviewPage = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const details = useCompanyReviewDetails(token);
  const respond = useRespondCompanyReview();
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [confirmingReject, setConfirmingReject] = useState(false);
  const [responseFailed, setResponseFailed] = useState(false);

  const send = (approve: boolean) => {
    if (!token) return;
    setResponseFailed(false);
    respond.mutate(
      { token, approve },
      {
        onSuccess: (decided) => {
          if (decided?.status === "WITHDRAWN") {
            setOutcome("alreadyDecided");
          } else {
            setOutcome(approve ? "approved" : "rejected");
          }
        },
        onError: (error) => {
          const status = statusOf(error);
          if (status === 404) setOutcome("invalid");
          else if (status === 409) setOutcome("alreadyDecided");
          else setResponseFailed(true);
        },
      }
    );
  };

  const notice = (
    kind: "success" | "error" | "info",
    key: Outcome | "error"
  ) => (
    <InlineNotification
      kind={kind}
      lowContrast
      hideCloseButton
      title={t(`companyReview.${key}.title`)}
      subtitle={t(`companyReview.${key}.message`)}
    />
  );

  const renderOutcome = (state: Outcome | "error") => {
    switch (state) {
      case "approved":
      case "rejected":
        return notice("success", state);
      case "alreadyDecided":
        return notice("info", state);
      case "invalid":
        return notice("error", state);
      default:
        return notice("error", "error");
    }
  };

  const renderQuestion = (review: CompanyReviewSummary) => (
    <>
      <h1 className="company-review__title">
        {t("companyReview.title", { company: review.companyName ?? "" })}
      </h1>
      <p>
        {t("companyReview.intro", {
          company: review.companyName ?? "",
          taxId: review.claimedTaxId ?? "",
        })}
      </p>
      <dl className="company-review__facts">
        <Fact label={t("companyReview.senderName")} value={review.senderName} />
        <Fact
          label={t("companyReview.senderEmail")}
          value={review.senderEmail}
        />
        <Fact
          label={t("companyReview.senderPhone")}
          value={review.senderPhone}
        />
        <Fact
          label={t("companyReview.buyer")}
          value={review.buyerCompanyName}
        />
        <Fact label={t("companyReview.request")} value={review.requestTitle} />
        <Fact
          label={t("companyReview.decideBy")}
          value={review.decideBy ? formatDate(review.decideBy) : null}
        />
      </dl>
      {OPEN.includes(review.status) ? (
        <>
          <p>{t("companyReview.ask")}</p>
          {responseFailed && notice("error", "error")}
          {confirmingReject ? (
            <>
              <p>{t("companyReview.confirmReject")}</p>
              <div className="company-review__actions">
                <Button
                  kind="danger"
                  disabled={respond.isPending}
                  onClick={() => send(false)}
                >
                  {t("companyReview.rejectConfirmed")}
                </Button>
                <Button
                  kind="secondary"
                  disabled={respond.isPending}
                  onClick={() => setConfirmingReject(false)}
                >
                  {t("companyReview.back")}
                </Button>
              </div>
            </>
          ) : (
            <div className="company-review__actions">
              <Button disabled={respond.isPending} onClick={() => send(true)}>
                {t("companyReview.approve")}
              </Button>
              <Button
                kind="danger--tertiary"
                disabled={respond.isPending}
                onClick={() => setConfirmingReject(true)}
              >
                {t("companyReview.reject")}
              </Button>
            </div>
          )}
        </>
      ) : (
        notice("info", "alreadyDecided")
      )}
    </>
  );

  const detailsState: Outcome | "error" | null = details.isError
    ? statusOf(details.error) === 404
      ? "invalid"
      : "error"
    : null;
  const state = outcome ?? detailsState;

  return (
    <AuthLayout width="wide">
      <div className="company-review">
        {state ? (
          renderOutcome(state)
        ) : details.isLoading || !details.data ? (
          <SkeletonText
            paragraph
            lineCount={6}
            aria-label={t("companyReview.loading")}
          />
        ) : (
          renderQuestion(details.data)
        )}
      </div>
    </AuthLayout>
  );
};

export default CompanyReviewPage;
